import isEmpty from 'lodash/isEmpty';
import { useMemo } from 'react';

import { useUserTrialDetails } from '@revelio/auth';
import {
  TALENT_DISCOVERY_DATA_V3,
  type TalentDiscoveryReqV3,
  type TalentDiscoveryRespV3,
} from '@revelio/data-access';

import { useCustomSkillTaxonomyTree } from '../../../../shared/selection-lists/hooks/useCustomSkillTaxonomyTree';
import { RESTRICTED_TRIAL_COLUMNS_MAP } from '../../constants/trialColumns';
import { useFilterContext } from '../../filters/contexts/FilterProvider';
import { useEffectiveFilters } from '../../filters/hooks/useEffectiveFilters';
import { useRequestGeographyTaxonomyId } from '../../filters/hooks/useRequestGeographyTaxonomyId';
import { FilterId } from '../../filters/types/filters';
import type { MapPoint } from '../Chart/Map/types';
import { buildTalentDiscoveryFiltersV3 } from './talentDiscovery/adapter';
import { generateRandomPlaceholder } from './talentDiscovery/helpers';
import {
  type DataProviderChildren,
  DataProviderQueryStatus,
  type DataProviderSet,
  DataType,
} from './types';
import { useUrqlQueryWithStatus } from './utils/data-provider-results-utils';
import { transformTalentDiscoveryMapData } from './utils/talent-discovery-map-utils';

type TalentDiscoveryData = {
  tableData?: DataProviderSet;
  mapData?: { points: MapPoint[] };
};

type TalentDiscoveryDataProviderProps = {
  exchangeRate?: number;
  children: DataProviderChildren<TalentDiscoveryData | undefined>;
};

export const TalentDiscoveryDataProvider = ({
  exchangeRate,
  children,
}: TalentDiscoveryDataProviderProps) => {
  const filters = useEffectiveFilters();
  const geographyTaxonomyId = useRequestGeographyTaxonomyId();
  const { isTrialUser } = useUserTrialDetails<boolean>({
    initialRenderValue: false,
  });

  // Wait for skill taxonomy registry to be populated before building filters
  // This ensures getCustomSkillLevelId returns valid values
  const { skillTaxonomyId, skillTaxonomyVersion } = useFilterContext();
  const { customSkillTree } = useCustomSkillTaxonomyTree(
    skillTaxonomyId,
    skillTaxonomyVersion
  );

  // Build v3 filters and variables
  const v3Variables: { query: TalentDiscoveryReqV3 } = useMemo(() => {
    const builtQuery = buildTalentDiscoveryFiltersV3(
      filters,
      geographyTaxonomyId
    );

    return {
      query: builtQuery,
    };
  }, [filters, geographyTaxonomyId]);

  // Check if we have any meaningful filters
  const hasFilters = useMemo(() => {
    const expr = v3Variables.query.filters.expr;
    if (!expr) return false;

    // Check each UserNode type
    if (expr.user_and) {
      return expr.user_and.children.length > 0;
    }

    if (expr.user_or) {
      return expr.user_or.children.length > 0;
    }

    if (expr.user_value) {
      return true;
    }

    if (expr.user_not) {
      return true;
    }

    return false;
  }, [v3Variables]);

  // If we have filters but built filters are empty and registry isn't ready,
  // we're waiting for the registry (because filters couldn't be built properly).
  // We check for skill-related filters that might need the registry.
  const hasPotentialSkillFilters = filters.some(
    (f) =>
      f.id === FilterId.CUSTOM_SKILL ||
      f.id === FilterId.MULTI_CUSTOM_SKILL ||
      f.id === FilterId.SKILL_TAXONOMY
  );

  const isWaitingForSkillsTaxonomy =
    !isEmpty(filters) &&
    customSkillTree === null &&
    !hasFilters &&
    hasPotentialSkillFilters;

  // V3 query
  const [{ data: v3Data, status: v3Status }] = useUrqlQueryWithStatus<
    { talent_discovery_search_v3: TalentDiscoveryRespV3 },
    { query: TalentDiscoveryReqV3 }
  >({
    query: TALENT_DISCOVERY_DATA_V3,
    variables: v3Variables,
    pause: !hasFilters || isWaitingForSkillsTaxonomy,
  });

  const status = v3Status;
  const response = v3Data?.talent_discovery_search_v3;

  const tableData: DataProviderSet | undefined = useMemo(() => {
    if (!hasFilters || status !== DataProviderQueryStatus.Success) {
      return undefined;
    }

    const users = response?.users || [];

    const rows = users
      .filter((u): u is NonNullable<(typeof users)[number]> => Boolean(u))
      .map((u) => ({
        __metaData: u,
        __typename: u.__typename,
        user_id: u.user_id,
        original_user_id: u.original_user_id,
        first_name: handleEmptyValue(u.first_name),
        last_name: handleEmptyValue(u.last_name),
        email: handleEmptyValue(u.email),
        personal_email: handleEmptyValue(u.personal_email),
        phone_number: handleEmptyValue(u.phone_number),
        current_title: handleEmptyValue(u.current_title),
        current_company: handleEmptyValue(u.current_company),
        start_date: handleEmptyValue(u.start_date),
        end_date: handleEmptyValue(u.end_date),
        gender: handleEmptyValue(u.gender),
        ethnicity: handleEmptyValue(u.ethnicity),
        highest_degree: handleEmptyValue(u.highest_degree),
        salary_base: handleEmptyValue(
          typeof u.salary_base === 'number' && exchangeRate
            ? u.salary_base * exchangeRate
            : u.salary_base
        ),
        salary_total: handleEmptyValue(
          typeof u.salary_total === 'number' && exchangeRate
            ? u.salary_total * exchangeRate
            : u.salary_total
        ),
        user_location: handleEmptyValue(u.user_location ?? u.location),
        prestige: handleEmptyValue(u.prestige),
        education: handleEmptyValue(u.education),
        remote_suitability: handleEmptyValue(u.remote_suitability),
        years_of_experience: handleEmptyValue(u.years_of_experience),
        flight_risk: handleEmptyValue(u.flight_risk),
        employment_relationship: handleEmptyValue(u.employment_relationship),
        work_schedule: handleEmptyValue(u.work_schedule),
        linkedin_url: handleEmptyValue(u.linkedin_url),
        graduation_year: handleEmptyValue(u.graduation_year),
        work_history: u.work_history ?? [],
        education_history: u.education_history ?? [],
        skills: u.skills ?? [],
      }));

    if (!isTrialUser) {
      return { type: DataType.TABLE_ROWS, data: rows };
    }

    // Blur cells for restricted columns for trial users
    type MaskedRow = (typeof rows)[number] & {
      __originalValues: Record<string, string>;
      __maskedValues: Record<string, string>;
    };

    const getRowSeed = (
      rowRecord: Record<string, unknown>,
      rowIndex: number
    ): string => {
      const toSeedValue = (value: unknown): string | null => {
        if (typeof value === 'number' && Number.isFinite(value)) {
          return String(value);
        }
        if (typeof value === 'string' && value.trim().length > 0) {
          return value.trim();
        }
        return null;
      };

      return (
        toSeedValue(rowRecord.user_id) ??
        toSeedValue(rowRecord.original_user_id) ??
        `row-${rowIndex}`
      );
    };

    const maskedRows: MaskedRow[] = rows.map((row, rowIndex) => {
      const originalValues: Record<string, string> = {};
      const maskedValues: Record<string, string> = {};
      const result: Record<string, unknown> = { ...row };
      const rowSeed = getRowSeed(row, rowIndex);

      Object.entries(RESTRICTED_TRIAL_COLUMNS_MAP).forEach(
        ([displayKey, backendKey]) => {
          const raw = row[displayKey as keyof typeof row];
          const value = typeof raw === 'string' ? raw : String(raw ?? '');
          if (value) {
            const maskedValue = generateRandomPlaceholder(backendKey, rowSeed);
            originalValues[displayKey] = value;
            maskedValues[displayKey] = maskedValue;
            result[displayKey] = maskedValue;
          } else {
            result[displayKey] = '';
          }
        }
      );

      return {
        ...result,
        __originalValues: originalValues,
        __maskedValues: maskedValues,
      } as MaskedRow;
    });

    return { type: DataType.TABLE_ROWS, data: maskedRows };
  }, [hasFilters, status, isTrialUser, response, exchangeRate]);

  const points: MapPoint[] = useMemo(() => {
    const raw = response?.map_data ?? [];
    return transformTalentDiscoveryMapData(raw);
  }, [response]);

  // Show loading immediately if waiting for registry (before building query variables)
  if (isWaitingForSkillsTaxonomy) {
    return children({
      data: { tableData: undefined, mapData: undefined },
      status: DataProviderQueryStatus.Fetching,
    });
  }

  if (!hasFilters) {
    return children({
      data: { tableData: undefined, mapData: undefined },
      status: DataProviderQueryStatus.Success,
    });
  }

  const finalStatus = status ?? DataProviderQueryStatus.Idle;

  const result = {
    data: { tableData, mapData: { points } },
    status: finalStatus,
  };

  return children(result);
};

const handleEmptyValue = (
  value: string | number | null | undefined
): string => {
  if (value === 'empty' || value === null || value === undefined) {
    return '';
  }
  return String(value);
};
